import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Game } from "./engine/Game";
import { ChallengeRouter } from "./ui/challenges/ChallengeRouter";
import { HUD } from "./ui/HUD";
import { FpsMeter } from "./ui/FpsMeter";
import { TouchControls } from "./ui/TouchControls";
import { SandboxDebugOverlay } from "./ui/SandboxDebugOverlay";
import type { GameConfig, GameEvent, RunOutcome, RunState } from "./types";
import type { WordDTO } from "@shared/types";
import { RUNNER } from "@shared/constants";
import { loadGameSettings, saveGameSettings, type GameSettings, type MovementMode } from "../local/gameSettings";
import { detectQuality, setQuality, type Quality } from "./engine/quality";
import { clearSession } from "../local/session";
import { levelIndexFromLessonId } from "../local/lessonId";
import { refreshAudioSettings, sfx, unlockAudioOnUserGesture } from "./audio";

interface RemotePos {
  id: string;
  s: number;
  laneOffset: number;
  state: string;
  updatedAt: number;
  velocityS?: number;
  velocityL?: number;
  jumpSeq?: number;
  y?: number;
  vy?: number;
  grounded?: boolean;
}

interface Props {
  config: GameConfig;
  onEnd: (outcome: RunOutcome) => void;
  /** Multiplayer: live position map keyed by player id (other players only). */
  positions?: Map<string, RemotePos>;
  /** Multiplayer: called when our own player moves (for broadcasting ticks). */
  onMyTick?: (
    s: number,
    laneOffset: number,
    state: string,
    velocityS: number,
    velocityL: number,
    jumpSeq?: number,
    y?: number,
    vy?: number,
    grounded?: boolean,
  ) => void;
  /** When true, the game is paused (e.g. while the 3-2-1 countdown plays). */
  paused?: boolean;
  /** Rendered as an in-world Three.js arcade countdown sign. */
  countdownLabel?: string | null;
  /** Hide the pause card during pre-start camera/countdown moments. */
  showPauseOverlay?: boolean;
  /** Optional ref the parent fills with spectator-control API once the game
   *  mounts. Used by RacePlay for after-finish "watch another player" UI. */
  spectatorRef?: React.MutableRefObject<SpectatorApi | null>;
  /** Multiplayer-only: host-clicks-"Back to lobby" from the pause overlay.
   *  When provided AND `isBattleHost` is true, the pause overlay shows a
   *  dedicated lobby-return button alongside Resume / Settings / Exit. */
  onBackToLobby?: () => void;
  /** Multiplayer: true if the local player is the room host. Gates the
   *  Back-to-Lobby button so joiners don't see something they can't act on. */
  isBattleHost?: boolean;
  /** Multiplayer: true while the host has paused the whole match. Every
   *  client receives this and freezes its game + shows a "Paused by host"
   *  overlay. Joiners see no Resume; only the host can clear it. */
  hostPaused?: boolean;
  /** Multiplayer-only callback the host uses to toggle the match pause.
   *  Provided exclusively to the host; undefined on joiners. */
  onHostPauseToggle?: (paused: boolean) => void;
}

export interface SpectatorApi {
  cycle: (dir: 1 | -1) => string | null;
  targetId: () => string | null;
  isSpectating: () => boolean;
}

export function GameView({
  config,
  onEnd,
  positions,
  onMyTick,
  paused,
  countdownLabel,
  spectatorRef,
  showPauseOverlay = true,
  onBackToLobby,
  isBattleHost = false,
  hostPaused = false,
  onHostPauseToggle,
}: Props) {
  const nav = useNavigate();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const gameRef = useRef<Game | null>(null);
  const onEndRef = useRef(onEnd);
  const positionsRef = useRef(positions);
  const onMyTickRef = useRef(onMyTick);
  const [state, setState] = useState<RunState>({
    hearts: RUNNER.startingHearts,
    stumbles: 0,
    coins: 0,
    score: 0,
    startedAt: performance.now(),
    finishedAt: null,
    distance: 0,
    paused: false,
    speed: RUNNER.baseSpeed,
    invulnerableUntil: 0,
  });
  const [wordsCorrect, setWordsCorrect] = useState(0);
  const [activeChallenge, setActiveChallenge] = useState<{
    word: WordDTO;
    distractors: WordDTO[];
    resolve: (correct: boolean) => void;
  } | null>(null);
  const [floats, setFloats] = useState<{ id: number; value: number }[]>([]);
  const [showRunSettings, setShowRunSettings] = useState(false);
  /** User-initiated pause request — independent of the auto-pause that
   *  fires during flyover / countdown. When true, the pause overlay is
   *  forced open even if the game was already programmatically paused. */
  const [userPaused, setUserPaused] = useState(false);
  const [gameSettings, setGameSettings] = useState<GameSettings>(() => loadGameSettings());
  const [airborne, setAirborne] = useState(false);

  useEffect(() => {
    let raf = 0;
    const poll = () => {
      raf = requestAnimationFrame(poll);
      const a = !!gameRef.current?.isAirborne();
      setAirborne((prev) => (prev === a ? prev : a));
    };
    raf = requestAnimationFrame(poll);
    return () => cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    onEndRef.current = onEnd;
    positionsRef.current = positions;
    onMyTickRef.current = onMyTick;
  }, [onEnd, positions, onMyTick]);

  // Hidden Shift+P shortcut to capture a level thumbnail. Downloads
  // `level-{N}.jpg` so it can be dropped into `apps/web/public/images/
  // levels/` to replace the picker's emoji with a real screenshot.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "P" || !e.shiftKey) return;
      const canvas = canvasRef.current;
      if (!canvas) return;
      // Match teaching-level index — extract from the lesson id when we
      // can; falls back to 0 (useful name still).
      const m = /-?L(\d+)/i.exec(config.lessonId ?? "");
      const idx = m ? Number(m[1]) : 0;
      canvas.toBlob((blob) => {
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `level-${idx}.jpg`;
        a.click();
        URL.revokeObjectURL(url);
      }, "image/jpeg", 0.85);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [config.lessonId]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const handleEvent = (e: GameEvent) => {
      switch (e.type) {
        case "state":
          setState(e.state);
          if (!e.state.paused) setShowRunSettings(false);
          break;
        case "coin": {
          const id = performance.now() + Math.random();
          setFloats((arr) => [...arr.slice(-12), { id, value: e.value }]);
          setTimeout(() => setFloats((arr) => arr.filter((f) => f.id !== id)), 900);
          break;
        }
        case "challenge": {
          const others = config.words.filter((w) => w.id !== e.word.id);
          const distractors = others.sort(() => Math.random() - 0.5).slice(0, 3);
          setActiveChallenge({ word: e.word, distractors, resolve: e.resolve });
          break;
        }
        case "finish": {
          // In multiplayer, transition into after-finish spectator mode:
          // hide our character, follow a friend who's still racing. The
          // game stays mounted until the server says results phase.
          const isMp = !!(positionsRef.current && positionsRef.current.size > 0);
          if (isMp) gameRef.current?.enterSpectator();
          onEndRef.current(e.outcome);
          break;
        }
        case "death":
          onEndRef.current(e.outcome);
          break;
      }
    };

    const game = new Game(canvas, config, handleEvent);
    gameRef.current = game;
    // Warm the Web Audio pipeline on the first pointerdown inside the
    // game canvas — covers MP "Start Race" paths that bypass the
    // lobby PLAY button's audio-unlock hook.
    const warmAudio = () => {
      // Static import now (was dynamic) — eliminates the Vite chunk
      // warning about audio.ts being both statically and dynamically
      // imported, and lets the audio module be reused from its
      // existing chunk instead of duplicated.
      try { unlockAudioOnUserGesture(); } catch { /* hardened against
        prerendered builds where the audio module hasn't loaded */ }
      canvas.removeEventListener("pointerdown", warmAudio);
    };
    canvas.addEventListener("pointerdown", warmAudio, { once: true });
    if (spectatorRef) {
      spectatorRef.current = {
        cycle: (dir) => game.cycleSpectator(dir),
        targetId: () => game.getSpectatorTargetId(),
        isSpectating: () => game.isSpectating(),
      };
    }

    // When the tab is hidden (iPad locked, home button pressed, Chrome
    // backgrounded) RAF stops, heartbeats slow, and multiplayer ghosts
    // freeze on peers. Auto-pause so the player doesn't take damage while
    // away. Resume when the tab comes back. The pause overlay UI also
    // reacts to runState.paused so it'll show automatically.
    const onVisibility = () => {
      if (document.hidden) {
        game.setPaused(true);
      } else {
        // Don't auto-resume — show the pause card so the player can
        // confirm. They press Resume to return to running. This avoids
        // catching them with their hands off the controls.
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    // Pump remote ghost positions into the game + broadcast our own.
    // Velocity sent to peers is the *actual* per-frame delta, not the
    // player's intended running speed. This matters whenever the player's
    // distance is overridden (moving platform, knockback slow):
    // ghost extrapolation on the other side needs to match real motion or
    // peers see violent snap-backs every snapshot.
    let mpRaf = 0;
    let lastBroadcastS: number | null = null;
    let lastBroadcastL: number | null = null;
    let lastBroadcastAt = 0;
    const pump = () => {
      mpRaf = requestAnimationFrame(pump);
      if (positionsRef.current) {
        for (const [id, p] of positionsRef.current) {
          game.setRemotePosition(id, p.s, p.laneOffset, p.state, p.velocityS ?? 0, p.velocityL ?? 0, p.jumpSeq, p.y, p.vy, p.grounded);
        }
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const gp: any = game as any;
      const ply = gp.player;
      if (ply) {
        const now = performance.now();
        const s = ply.s ?? 0;
        const l = ply.laneOffset ?? 0;
        let velocityS = 0;
        let velocityL = 0;
        if (lastBroadcastS != null && lastBroadcastL != null) {
          const dtSec = Math.max(0.001, (now - lastBroadcastAt) / 1000);
          const ds = s - lastBroadcastS;
          const dl = l - lastBroadcastL;
          // Skip velocity computation when a frame stalled (dt > 80ms) or
          // when the position teleported (platform attach, respawn, finish).
          // A garbage velocity here would propagate to peers and cause big
          // extrapolation overshoots and snap-backs.
          const teleported = Math.abs(ds) > 2.5 || Math.abs(dl) > 2.0;
          const stalled = dtSec > 0.08;
          if (!teleported && !stalled) {
            velocityS = Math.max(-30, Math.min(30, ds / dtSec));
            velocityL = Math.max(-20, Math.min(20, dl / dtSec));
          }
        }
        lastBroadcastS = s;
        lastBroadcastL = l;
        lastBroadcastAt = now;
        // Override state to "falling" while the game is animating the fall —
        // remote ghosts replay a matching fall animation so the death is
        // visible to everyone instead of an instant teleport to the respawn.
        const remoteState = gp.isFalling?.() ? "falling" : (ply.state ?? "run");
        onMyTickRef.current?.(
          s,
          l,
          remoteState,
          velocityS,
          velocityL,
          ply.jumpSeq,
          ply.y,
          ply.vy,
          ply.isGroundedOnSurface?.(),
        );
      }
    };
    if (positionsRef.current || onMyTickRef.current) mpRaf = requestAnimationFrame(pump);

    return () => {
      cancelAnimationFrame(mpRaf);
      document.removeEventListener("visibilitychange", onVisibility);
      game.dispose();
      gameRef.current = null;
      if (spectatorRef) spectatorRef.current = null;
    };
    // ─── End of mount effect. ───
    // Only re-create the Game when the truly identity-defining bits of the
    // config change — never on a remote-roster delta (those used to flip
    // the config object reference every player join/leave broadcast, which
    // caused the "course resets mid-race" bug in 8-player battles). All
    // mutable fields (remotePlayers, words, etc.) are kept current via
    // refs + imperative APIs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    config.lessonId,
    config.seed,
    config.clockOrigin,
    config.courseLength,
    config.courseMode,
    config.mySlotIdx,
  ]);

  // Keep the ghost roster in sync without rebuilding the Game when remote
  // players come and go.
  useEffect(() => {
    if (!gameRef.current) return;
    if (!config.remotePlayers) return;
    gameRef.current.syncRemotePlayers(config.remotePlayers);
  }, [config.remotePlayers]);

  // External pause control (countdown overlay).
  useEffect(() => {
    if (paused === undefined) return;
    gameRef.current?.setPaused(paused);
    if (!paused) setShowRunSettings(false);
  }, [paused]);

  // In-world countdown sign, not a DOM popup.
  useEffect(() => {
    gameRef.current?.setCountdownLabel(countdownLabel ?? null);
    // Survival arena: fire the "BATTLE ROYALE" 3D banner + epic echo
    // announcement on the first countdown beat so it builds into "GO!".
    if (countdownLabel === "3" && config.courseMode === "double-spinner") {
      gameRef.current?.startSurvivalIntro();
    }
  }, [countdownLabel]);

  const handleAnswer = (correct: boolean) => {
    activeChallenge?.resolve(correct);
    if (correct) setWordsCorrect((n) => n + 1);
    setActiveChallenge(null);
  };

  const applyRunSettings = (patch: Partial<GameSettings>) => {
    const next = { ...gameSettings, ...patch };
    setGameSettings(next);
    saveGameSettings(next);
    refreshAudioSettings();
    gameRef.current?.updateSettings(next);
    if (patch.audioVolume !== undefined || patch.movementMode !== undefined) sfx.menu();
  };

  const resumeGame = () => {
    setShowRunSettings(false);
    setUserPaused(false);
    // Battle host: clear the room-wide pause so every joiner resumes too.
    if (onHostPauseToggle && hostPaused) {
      onHostPauseToggle(false);
      // setPaused(false) skipped — the room state update will flow back
      // through the `paused` prop effect and clear the engine pause.
      return;
    }
    gameRef.current?.setPaused(false);
  };

  return (
    <div className="game-root">
      <canvas ref={canvasRef} className="game-canvas" />
      {gameSettings.showFps && <FpsMeter />}
      <SandboxDebugOverlay game={gameRef.current} />
      <HUD
        state={state}
        totalDistance={config.courseLength}
        levelIndex={levelIndexFromLessonId(config.lessonId)}
        wordsCorrect={wordsCorrect}
        wordsTotal={config.words.length}
        currentWord={activeChallenge?.word.english}
        /* Survival has no timer — it runs until you fall (last to fall wins). */
        survivalRoundEndsAt={undefined}
        onPause={() => {
          // Battle host: pausing toggles the WHOLE room. Every client
          // freezes via state.hostPaused. The host still sees their
          // local pause-menu overlay so they can hit Settings / Exit /
          // Back-to-lobby, but resume goes through the room-pause
          // toggle so joiners also resume.
          if (onHostPauseToggle) {
            const next = !hostPaused;
            onHostPauseToggle(next);
            if (next) setUserPaused(true);
            return;
          }
          // Joiner / solo: just toggle the local engine pause.
          if (state.paused) setUserPaused(true);
          else gameRef.current?.togglePause();
        }}
      />
      {/* TouchControls also show during the 3-2-1 countdown so the player
       *  can grab the joystick + get oriented before GO. The engine is
       *  paused during the countdown, so any input collected just sits
       *  in `moveAxis/forwardAxis` and is consumed the moment the GO
       *  triggers the normal update loop. */}
      {!activeChallenge && (
        <TouchControls
          onMove={(x, y) => gameRef.current?.setMoveVector(x, y)}
          onJump={() => gameRef.current?.jump()}
          airborne={airborne}
          deadzone={gameSettings.controllerDeadzone}
          sensitivity={gameSettings.movementSensitivity}
          jumpCooldown01={() => gameRef.current?.getJumpCooldown01() ?? 0}
        />
      )}
      {activeChallenge && (
        <ChallengeRouter
          word={activeChallenge.word}
          distractors={activeChallenge.distractors}
          onAnswer={handleAnswer}
          rotationSeed={config.seed}
          allowedKinds={config.challengeKinds}
        />
      )}
      {/* Joiner view of a host-driven pause — no menu, just a banner so
       *  the player knows the freeze is intentional. */}
      {hostPaused && !isBattleHost && !activeChallenge && !countdownLabel && (
        <div className="pause-overlay">
          <div className="pause-menu">
            <div className="pause-menu-title">⏸ Paused by host</div>
            <div className="pause-menu-actions">
              <button
                className="pause-btn pause-btn-exit"
                onClick={() => { clearSession(); nav("/play"); }}
              >
                ← Leave room
              </button>
            </div>
          </div>
        </div>
      )}
      {(showPauseOverlay || userPaused) && state.paused && !activeChallenge && !countdownLabel && !(hostPaused && !isBattleHost) && (
        <div className="pause-overlay">
          {!showRunSettings && (
            <div className="pause-menu">
              <div className="pause-menu-title">{hostPaused ? "⏸ Match paused" : "Paused"}</div>
              <div className="pause-menu-actions">
                <button className="pause-btn pause-btn-resume" onClick={resumeGame}>
                  ▶ Resume
                </button>
                <button className="pause-btn pause-btn-settings" onClick={() => setShowRunSettings(true)}>
                  ⚙ Settings
                </button>
                {isBattleHost && onBackToLobby && (
                  // Battle host only: bail out of the race mid-run and
                  // bring every connected device back to the lobby.
                  <button
                    className="pause-btn pause-btn-lobby"
                    onClick={() => {
                      onBackToLobby();
                      setUserPaused(false);
                    }}
                  >
                    🏁 Back to lobby
                  </button>
                )}
                <button className="pause-btn pause-btn-exit" onClick={() => { clearSession(); nav("/play"); }}>
                  ← Exit
                </button>
              </div>
            </div>
          )}
          {showRunSettings && (
            <RunSettingsOverlay
              settings={gameSettings}
              onChange={applyRunSettings}
              onBack={() => setShowRunSettings(false)}
            />
          )}
        </div>
      )}
    </div>
  );
}

function RunSettingsOverlay({
  settings,
  onChange,
  onBack,
}: {
  settings: GameSettings;
  onChange: (patch: Partial<GameSettings>) => void;
  onBack: () => void;
}) {
  const setMovementMode = (mode: MovementMode) => onChange({ movementMode: mode });
  return (
    <div className="run-settings-panel" role="dialog" aria-label="Game settings">
      <div className="run-settings-top">
        <button className="run-settings-back" onClick={onBack} aria-label="Back to pause menu">←</button>
        <h2>Settings</h2>
      </div>
      <div className="run-settings-grid">
        <div className="run-settings-section">
          <h3>Controls</h3>
          <div className="segmented">
            <button
              className={settings.movementMode === "free" ? "active" : ""}
              onClick={() => setMovementMode("free")}
            >
              Free
            </button>
            <button
              className={settings.movementMode === "lanes" ? "active" : ""}
              onClick={() => setMovementMode("lanes")}
            >
              Lanes
            </button>
          </div>
          <RunSlider
            label="Movement sensitivity"
            value={settings.movementSensitivity}
            min={0.25}
            max={1.5}
            onChange={(movementSensitivity) => onChange({ movementSensitivity })}
          />
          <RunSlider
            label="Controller deadzone"
            value={settings.controllerDeadzone}
            min={0.05}
            max={0.4}
            onChange={(controllerDeadzone) => onChange({ controllerDeadzone })}
          />
          <RunSlider
            label="Camera sensitivity"
            value={settings.cameraSensitivity}
            min={0.25}
            max={1.5}
            onChange={(cameraSensitivity) => onChange({ cameraSensitivity })}
          />
        </div>
        <div className="run-settings-section">
          <h3>Sound</h3>
          <RunSlider
            label="Voice and SFX"
            value={settings.audioVolume}
            min={0}
            max={1}
            onChange={(audioVolume) => onChange({ audioVolume })}
          />
          <RunSlider
            label="Menu music"
            value={settings.musicVolume}
            min={0}
            max={1}
            onChange={(musicVolume) => onChange({ musicVolume })}
          />
        </div>
        <div className="run-settings-section">
          <h3>🎨 Graphics</h3>
          <p className="t-muted" style={{ marginTop: 0, fontSize: "0.85rem" }}>
            Lower the setting on older tablets if the game runs slow. Takes
            effect on the next run / level reload.
          </p>
          <QualityPicker />
        </div>
        <div className="run-settings-section" style={{ gridColumn: "1 / -1" }}>
          <h3>🧸 Kids assist</h3>
          <p
            className="t-muted"
            style={{ margin: "0 0 8px", fontSize: "0.85rem", textTransform: "none" }}
          >
            Auto-runs and auto-jumps. The joystick still steers left and right.
          </p>
          <button
            className={settings.assistMode ? "btn btn-red btn-block" : "btn btn-primary btn-block"}
            onClick={() => onChange({ assistMode: !settings.assistMode })}
          >
            {settings.assistMode ? "Turn off assist" : "Turn on assist"}
          </button>
        </div>
      </div>
    </div>
  );
}

function RunSlider({
  label,
  value,
  min,
  max,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
}) {
  return (
    <label className="settings-slider run-settings-slider">
      <span>{label}</span>
      <strong>{Math.round(value * 100)}%</strong>
      <input
        type="range"
        min={Math.round(min * 100)}
        max={Math.round(max * 100)}
        value={Math.round(value * 100)}
        onChange={(e) => onChange(Number(e.target.value) / 100)}
      />
    </label>
  );
}

/** Graphics-quality picker — Low / Medium / High. Persists to
 *  localStorage via `setQuality`; takes effect on the next run.
 *  Exported so the main Settings page can mount the same control. */
export function QualityPicker() {
  const [q, setQ] = useState<Quality>(() => detectQuality());
  // Reload so EVERY 3D surface (menu hero, thumbnails, avatar, game) rebuilds
  // at the chosen tier — otherwise already-mounted menus keep the old quality.
  const pick = (next: Quality) => {
    if (next === q) return;
    setQuality(next);
    setQ(next);
    setTimeout(() => window.location.reload(), 120);
  };
  return (
    <div className="segmented quality-picker">
      <button className={q === "low" ? "active" : ""} onClick={() => pick("low")}>
        🪶 Low
      </button>
      <button className={q === "medium" ? "active" : ""} onClick={() => pick("medium")}>
        ✨ Medium
      </button>
      <button className={q === "high" ? "active" : ""} onClick={() => pick("high")}>
        🎮 High
      </button>
    </div>
  );
}
